import type { ReactElement, ReactNode, RefObject } from 'react';
import { calendarLocale } from './calendar_locale.js';
import { weekdayNarrow } from '@voll-intelligence/dates';
import { DateSheetBody } from './date_sheet_body.js';
import type { TravelCalendarBaseProps } from './travel_calendar.js';

/*
 * As MOLDURAS do `TravelCalendar` — a folha e o popover em volta da grade. Saíram
 * de `travel_calendar.tsx` pelo teto de linhas dele.
 */

type FrameProps = Pick<TravelCalendarBaseProps, 'busy' | 'footer'> & {
  readonly children: ReactNode;
};

/** A linha D S T Q Q S S pregada sob o cabeçalho da folha (#5662), na mesma
 *  grade de 7 colunas e no mesmo recuo da grade dos meses. Começa no dia da
 *  semana do idioma (o `es` abre na segunda, como a grade). Só visual, como o
 *  cabeçalho de colunas do rdp que ela substitui. */
function SheetWeekdays({ locale }: { readonly locale: string }): ReactElement {
  const weekStartsOn = calendarLocale(locale).options?.weekStartsOn ?? 0;
  // 4 jan 2026 é um domingo: a semana conta a partir dele.
  const days = Array.from({ length: 7 }, (_, i) => new Date(2026, 0, 4 + weekStartsOn + i));
  return (
    <div
      aria-hidden="true"
      data-slot="date-sheet-weekdays"
      className="grid h-10 grid-cols-7 items-center px-4 text-center text-sm text-muted-foreground select-none"
    >
      {days.map((day) => (
        <span key={day.getDay()}>{weekdayNarrow(day, locale)}</span>
      ))}
    </div>
  );
}

/** A moldura da FOLHA: a linha dos dias pregada, o rolo e o rodapé do `DateSheetBody`. */
export function SheetFrame({
  scrollerRef,
  locale,
  busy,
  footer,
  children,
}: FrameProps &
  Pick<TravelCalendarBaseProps, 'locale'> & {
    /** O rolo da coluna — o calendário mede por ele quando crescer. */
    readonly scrollerRef: RefObject<HTMLDivElement | null>;
  }): ReactElement {
  return (
    <div className="flex min-h-0 flex-1 flex-col" aria-busy={busy || undefined}>
      <DateSheetBody
        scrollerRef={scrollerRef}
        header={<SheetWeekdays locale={locale} />}
        {...(footer ? { footer } : {})}
      >
        {children}
      </DateSheetBody>
    </div>
  );
}

/** A moldura do POPOVER e do cartão: a grade e o rodapé no fluxo. */
export function PopoverFrame({
  card,
  busy,
  footer,
  children,
}: FrameProps & { readonly card: boolean }): ReactElement {
  return (
    /* O cartão é o contêiner que a célula mede (`cqw`). */
    <div className={card ? '@container grid gap-2' : 'grid gap-2'} aria-busy={busy || undefined}>
      {children}
      {/* `flex-wrap` e não `min-w-max`: com um mês a frase e o `Confirmar` não
          cabem numa linha de ~280px, e o `min-w-max` empurrava o botão pra fora
          do popover, cortado. */}
      {footer && (
        <footer
          className={
            card
              ? 'flex flex-col gap-3 border-t border-border px-1 pt-3'
              : 'flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-border px-1 pt-2'
          }
        >
          {footer}
        </footer>
      )}
    </div>
  );
}
